<script lang="ts">
  /** Profile dropdown — shown when the user is signed in. Plain link
   *  to /inventory for sign-in when signed out. */
  import { session } from '$domain/session.svelte';
  import { navigate } from '$router/router.svelte';
  import { logout } from '$api/auth';

  let open = $state(false);
  let menuEl: HTMLDivElement | null = $state(null);

  function close(): void {
    open = false;
  }

  function onDocClick(e: MouseEvent): void {
    if (!open) return;
    if (menuEl && menuEl.contains(e.target as Node)) return;
    close();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  $effect(() => {
    if (typeof document === 'undefined') return;
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  });

  function handleSignOut(): void {
    void logout();
    close();
    navigate('/');
  }

  /** Two-letter initials for the avatar circle. Bungie display names
   *  often have a #1234 suffix; strip that for the initials. */
  function initials(name: string | undefined): string {
    if (!name) return '?';
    const clean = name.split('#')[0] ?? name;
    const parts = clean.trim().split(/\s+/);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
</script>

{#if session.user}
  <div class="profile-menu" bind:this={menuEl}>
    <button
      type="button"
      class="profile-trigger"
      onclick={() => (open = !open)}
      aria-haspopup="menu"
      aria-expanded={open}
    >
      <span class="avatar" aria-hidden="true">{initials(session.user.displayName)}</span>
      <span class="name">{session.user.displayName}</span>
    </button>

    {#if open}
      <div class="profile-popover" role="menu">
        <header>
          <span class="pp-name">{session.user.displayName}</span>
          <code class="pp-id">{session.user.membershipId}</code>
        </header>
        <a href="#/inventory" onclick={close} role="menuitem">My inventory</a>
        <a href="#/wizard" onclick={close} role="menuitem">New build</a>
        {#if session.isAdmin}
          <a href="#/admin" onclick={close} role="menuitem" class="admin-link">Admin</a>
        {/if}
        <hr />
        <button type="button" onclick={handleSignOut} role="menuitem">Sign out</button>
      </div>
    {/if}
  </div>
{:else}
  <a href="#/inventory" class="sign-in">sign in</a>
{/if}

<style>
  .profile-menu {
    position: relative;
  }
  .profile-trigger {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: transparent;
    border: 1px solid var(--border);
    color: var(--fg);
    padding: 4px var(--space-2) 4px 4px;
    border-radius: var(--radius-full);
    cursor: pointer;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-transform: lowercase;
    transition: border-color var(--speed-fast) var(--easing);
  }
  .profile-trigger:hover {
    border-color: var(--accent);
  }
  .avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--bg);
    font-weight: 700;
    font-size: 10px;
    letter-spacing: 0;
  }
  .name {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .profile-popover {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    min-width: 220px;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-3);
    padding: var(--space-2);
    z-index: var(--z-overlay);
    box-shadow: 0 16px 32px -12px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    gap: 2px;
    animation: pp-in 140ms var(--easing);
  }
  @keyframes pp-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .profile-popover header {
    display: flex;
    flex-direction: column;
    padding: var(--space-2) var(--space-3) var(--space-3);
    border-bottom: 1px solid var(--border);
    margin-bottom: var(--space-1);
  }
  .pp-name {
    font-size: var(--text-sm);
    color: var(--fg);
  }
  .pp-id {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--fg-subtle);
    word-break: break-all;
  }
  .profile-popover a,
  .profile-popover button {
    background: transparent;
    border: 0;
    color: var(--fg);
    text-align: left;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-2);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-transform: lowercase;
    cursor: pointer;
    text-decoration: none;
    transition: background var(--speed-fast) var(--easing);
  }
  .profile-popover a:hover,
  .profile-popover button:hover {
    background: var(--bg-elev-3);
    color: var(--accent);
  }
  .admin-link {
    color: var(--accent) !important;
  }
  hr {
    border: 0;
    border-top: 1px solid var(--border);
    margin: var(--space-1) 0;
  }
  .sign-in {
    color: var(--accent);
    font-family: var(--font-mono);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: var(--text-xs);
  }
</style>
